import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/layout/Header";
import { SEO } from "@/components/seo/SEO";

const CompanyRegistration = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    sector: "",
    employeeCount: "",
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const sectors = [
    "Technologie",
    "Santé",
    "Finance",
    "Éducation",
    "Commerce",
    "Industrie",
    "Services",
    "Autre"
  ];

  const employeeCounts = [
    "1-10",
    "11-50",
    "51-200",
    "201-500",
    "500+"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Create user account
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
          }
        }
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        // Try to get or create the profile
        let profileData = null;
        
        // First try to get existing profile
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', authData.user.id)
          .maybeSingle();
        
        if (existingProfile) {
          profileData = existingProfile;
        } else {
          // If no profile exists, create one
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert({
              user_id: authData.user.id,
              email: authData.user.email || formData.email,
              first_name: formData.firstName,
              last_name: formData.lastName
            })
            .select('id')
            .single();
          
          if (createError) throw createError;
          profileData = newProfile;
        }
        
        // Update profile with current data if it already existed
        if (existingProfile) {
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              first_name: formData.firstName,
              last_name: formData.lastName
            })
            .eq('user_id', authData.user.id);

          if (profileError) throw profileError;
        }
        
        if (!profileData) {
          throw new Error('Impossible de créer le profil utilisateur. Veuillez réessayer.');
        }

        // Create company request with company details (no company_id yet)
        const { error: requestError } = await supabase
          .from('company_requests')
          .insert({
            company_id: null, // Will be set when admin approves
            requester_id: profileData.id,
            company_name: formData.companyName,
            company_sector: formData.sector,
            company_employee_count: parseInt(formData.employeeCount.split('-')[0]) || 500,
            status: 'pending'
          });

        if (requestError) throw requestError;

        // Send confirmation and notification emails
        try {
          const { error: emailError } = await supabase.functions.invoke('send-company-registration-emails', {
            body: {
              companyName: formData.companyName,
              companyEmail: formData.email,
              firstName: formData.firstName,
              lastName: formData.lastName,
            }
          });

          if (emailError) {
            console.error('Email sending error:', emailError);
          }
        } catch (emailError) {
          console.error('Failed to send emails:', emailError);
        }

        toast({
          title: "Demande envoyée !",
          description: "Votre demande de création de compte entreprise a été envoyée à l'administrateur pour validation.",
        });

        await supabase.auth.signOut();
        navigate('/');
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de l'envoi de votre demande",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Créer un compte entreprise | Renest"
        description="Demandez l'ouverture de votre compte entreprise Renest et proposez le télétravail international à vos équipes en toute simplicité."
        canonical="https://renest.fr/company-registration"
      />
      
      <div className="min-h-screen bg-gradient-subtle">
        <Header />
        <div className="container mx-auto py-16 px-4">
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Créer un compte entreprise</CardTitle>
              <CardDescription className="text-center">
                Remplissez le formulaire ci-dessous pour demander la création d'un compte entreprise
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({...prev, firstName: e.target.value}))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({...prev, lastName: e.target.value}))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyName">Nom de l'entreprise *</Label>
                  <Input
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({...prev, companyName: e.target.value}))}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Secteur d'activité *</Label>
                    <Select
                      value={formData.sector}
                      onValueChange={(value) => setFormData(prev => ({...prev, sector: value}))}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un secteur" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map(sector => (
                          <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre d'employés *</Label>
                    <Select
                      value={formData.employeeCount}
                      onValueChange={(value) => setFormData(prev => ({...prev, employeeCount: value}))}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez la taille" />
                      </SelectTrigger>
                      <SelectContent>
                        {employeeCounts.map(count => (
                          <SelectItem key={count} value={count}>{count} employés</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email professionnel *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                    placeholder="votre.email@entreprise.com"
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    Cet email sera votre identifiant de connexion
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                    placeholder="Minimum 6 caractères"
                    minLength={6}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                  {isLoading ? "Envoi en cours..." : "Envoyer la demande"}
                </Button>

                <p className="text-sm text-muted-foreground text-center">
                  Votre demande sera examinée par notre équipe sous 48h.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default CompanyRegistration;
